import { useLoadScript } from '@react-google-maps/api'
import { useCallback, useEffect, useReducer, useRef } from 'react'

export const DEFAULT_MAP_CENTER = { lat: 51.5074, lng: -0.1278 }

export const DefaultConfig = {
  defaultCenter: DEFAULT_MAP_CENTER,
  defaultZoom: 19,
  infoDivStyle: {
    background: `white`,
    minWidth: '300px',
  },
  pathOptions: {
    strokeColor: '#530eea',
    strokeOpacity: 0.4,
    strokeWeight: 6,
    fillColor: '#c90eea',
    fillOpacity: 0.35,
    clickable: false,
    draggable: false,
    editable: false,
    visible: true,
    radius: 30000,
    // paths: __paths,
    zIndex: 1,
  },
}

const defaultLibraries = ['places'];


function reducer(currentState, newState) {
  return { ...currentState, ...newState }
}

function useSematicsGoogleMap() {

  const mapRef = useRef()
  const mapIdleRef = useRef(false)
  const clustererRef = useRef()
  const repaintTimerRef = useRef()

  const { isLoaded } = useLoadScript({
    version: '3',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_API_KEY,
    libraries : defaultLibraries,
  });

  const [{ pathCoords, markers, selectedMarkerData, mapReady, mapIdle }, setState] = useReducer(reducer, {
    pathCoords: [],
    markers: [],
    selectedMarkerData: undefined,
    mapReady: false,
    mapIdle: false,
  })

  const onMapLoadCallback = useCallback(function onMapLoadCallback(map) {
    if (map) {
      mapRef.current = map
      setState({
        mapReady: true,
      })
    }
  }, [])

  /**
   * Fires on the map's first `idle` event, i.e. once it has laid out and
   * computed its bounds. Clustered markers must not mount before this:
   * MarkerClusterer reads `map.getBounds()` when a marker is added, and until
   * the first idle that is undefined, which makes the clusterer throw
   * "Cannot read properties of undefined (reading 'lat')".
   */
  const onMapIdleCallback = useCallback(function onMapIdleCallback() {
    if (!mapIdleRef.current) {
      mapIdleRef.current = true
      setState({
        mapIdle: true,
      })
    }
  }, [])

  const onClustererLoad = useCallback(function onClustererLoad(clusterer) {
    clustererRef.current = clusterer
  }, [])

  /**
   * Re-clusters the markers, but only while the map has real bounds.
   *
   * Markers are rendered with `noClustererRedraw`, so adding one does not
   * trigger the clusterer's own redraw. That redraw reads `map.getBounds()`
   * and crashes with "Cannot read properties of undefined (reading 'lat')"
   * whenever the map is hidden (for instance while a Suspense boundary above
   * it shows its fallback, which hides the page with display:none). When the
   * bounds are missing we skip; the clusterer redraws itself on the map's
   * next `idle` event, which fires once it is visible again.
   *
   * The repaint is deferred to the next macrotask and coalesced, so a batch
   * of markers mounting (each calling this from `onLoad`) repaints once.
   */
  const repaintClusterer = useCallback(function repaintClusterer() {
    window.clearTimeout(repaintTimerRef.current)
    repaintTimerRef.current = window.setTimeout(() => {
      const clusterer = clustererRef.current
      const map = mapRef.current
      if (clusterer && map && map.getBounds()?.getNorthEast()) {
        clusterer.repaint()
      }
    }, 0)
  }, [])

  useEffect(() => () => window.clearTimeout(repaintTimerRef.current), [])

  const onMarkerClick = useCallback((markerItem) => {
    setState({
      selectedMarkerData: { ...markerItem },
    })
  }, [])

  const onMarkerClose = useCallback(() => {
    setState({
      selectedMarkerData: undefined,
    })
  }, [])

  const onMapClick = useCallback(() => {
    setState({
      selectedMarkerData: undefined,
    })
  }, [])


  return {
    isMapLoaded: isLoaded,
    onMarkerClick,
    onMapClick,
    onMarkerClose,
    onMapLoadCallback,
    onMapIdleCallback,
    mapIdle,
    onClustererLoad,
    repaintClusterer,
    DefaultConfig,
    pathCoords,
    markers,
    selectedMarkerData,
    mapReady,
    mapRef,
  }

}


export default useSematicsGoogleMap;
